import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { handleAuthError } from '../utils/authUtils';
import { rateLimiter } from '../utils/rateLimiter';

const API_BASE_URL = '/api';

export interface ApiResponse {
  success: boolean;
  error?: {
    code?: string;
    message: string;
    details?: any;
  };
}

export interface PaginationResponse {
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export abstract class BaseApiService {
  protected getAuthHeaders(correlationId?: string): Record<string, string> {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    if (correlationId) {
      headers['X-Correlation-ID'] = correlationId;
    }

    return headers;
  }

  protected handleError(error: any): never {
    if (handleAuthError(error)) {
      throw new Error('Authentication required');
    }

    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }

    throw new Error(error.message || 'An unexpected error occurred');
  }

  protected buildQueryParams(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  protected async request<T>(
    endpoint: string,
    options: AxiosRequestConfig = {},
    correlationId?: string
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    if (correlationId) {
      console.log(`[Frontend] Making request to ${endpoint} with correlation ID: ${correlationId}`);
    }

    return rateLimiter.enqueue(url, async () => {
      try {
        const headers = this.getAuthHeaders(correlationId);

        const config: AxiosRequestConfig = {
          headers: {
            ...headers,
            ...options.headers,
          },
          ...options,
        };

        const response: AxiosResponse = await axios({
          url,
          ...config,
        });

        return response.data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  protected async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    correlationId?: string
  ): Promise<T> {
    const queryString = params ? this.buildQueryParams(params) : '';
    return this.request<T>(`${endpoint}${queryString}`, { method: 'GET' }, correlationId);
  }

  protected async post<T>(endpoint: string, data?: any, correlationId?: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', data }, correlationId);
  }

  protected async put<T>(endpoint: string, data?: any, correlationId?: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', data }, correlationId);
  }

  protected async delete<T>(endpoint: string, correlationId?: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, correlationId);
  }
}
